import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { translatorAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Save, CheckCircle, AlertCircle } from 'lucide-react';

const TranslatorProfile = () => {
  const { user } = useOutletContext();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    personal_email: '',
    phone: '',
    pan: '',
    bank_account_holder: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    is_gst_vendor: false,
    gstin: '',
    address: '',
    state: '',
  });
  
  useEffect(() => {
    loadProfile();
  }, []);
  
  const loadProfile = async () => {
    try {
      const response = await translatorAPI.getProfile();
      if (response.data) {
        setProfile(response.data);
        setFormData({
          full_name: response.data.full_name || '',
          personal_email: response.data.personal_email || user?.email || '',
          phone: response.data.phone || '',
          pan: response.data.pan || '',
          bank_account_holder: response.data.bank_account_holder || '',
          bank_account_number: response.data.bank_account_number || '',
          bank_ifsc: response.data.bank_ifsc || '',
          bank_name: response.data.bank_name || '',
          is_gst_vendor: response.data.is_gst_vendor || false,
          gstin: response.data.gstin || '',
          address: response.data.address || '',
          state: response.data.state || '',
        });
      } else {
        setFormData(prev => ({ ...prev, personal_email: user?.email || '' }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await translatorAPI.createProfile(formData);
      toast.success('Profile saved successfully!');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">My Profile</h1>
          <p className="text-muted-foreground">Complete your profile information for invoicing and payments</p>
        </div>
        
        {profile?.company_email && (
          <Card className="border-l-4 border-l-success bg-success/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium text-success">Company Email Assigned</p>
                  <p className="text-sm text-muted-foreground mt-1">{profile.company_email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {!profile?.nda_signed && (
          <Card className="border-l-4 border-l-warning bg-warning/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-warning mt-0.5" />
                <div>
                  <p className="font-medium text-warning">NDA Signature Required</p>
                  <p className="text-sm text-muted-foreground mt-1">You must sign the NDA before receiving work assignments. Go to NDA page to sign.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    required
                    data-testid="input-full-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="personal_email">Personal Email *</Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => handleChange('personal_email', e.target.value)}
                    required
                    data-testid="input-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    data-testid="input-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pan">PAN Number *</Label>
                  <Input
                    id="pan"
                    value={formData.pan}
                    onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                    maxLength={10}
                    required
                    data-testid="input-pan"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="input-address"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  data-testid="input-state"
                />
              </div>
            </CardContent>
          </Card>
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Bank Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_account_holder">Account Holder Name *</Label>
                  <Input
                    id="bank_account_holder"
                    value={formData.bank_account_holder}
                    onChange={(e) => handleChange('bank_account_holder', e.target.value)}
                    required
                    data-testid="input-account-holder"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account Number *</Label>
                  <Input
                    id="bank_account_number"
                    value={formData.bank_account_number}
                    onChange={(e) => handleChange('bank_account_number', e.target.value)}
                    required
                    data-testid="input-account-number"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bank_ifsc">IFSC Code *</Label>
                  <Input
                    id="bank_ifsc"
                    value={formData.bank_ifsc}
                    onChange={(e) => handleChange('bank_ifsc', e.target.value.toUpperCase())}
                    required
                    data-testid="input-ifsc"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name *</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    required
                    data-testid="input-bank-name"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>GST Information (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_gst_vendor"
                  checked={formData.is_gst_vendor}
                  onCheckedChange={(checked) => handleChange('is_gst_vendor', checked)}
                  data-testid="checkbox-gst-vendor"
                />
                <Label htmlFor="is_gst_vendor" className="cursor-pointer">
                  I am a GST registered vendor
                </Label>
              </div>
              
              {formData.is_gst_vendor && (
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN *</Label>
                  <Input
                    id="gstin"
                    value={formData.gstin}
                    onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                    required={formData.is_gst_vendor}
                    maxLength={15}
                    data-testid="input-gstin"
                  />
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="mt-6 flex justify-end">
            <Button 
              type="submit" 
              disabled={saving}
              size="lg"
              className="bg-accent hover:bg-accent/90"
              data-testid="btn-save-profile"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default TranslatorProfile;
